import { Decimal } from 'decimal.js';

/**
 * Danish formatting for the values the API returns as strings.
 *
 * ## Why this does not use Intl.NumberFormat
 *
 * `Intl.NumberFormat` takes a `number`. Every amount here arrives from the API
 * as a decimal **string** precisely so it never becomes a binary float, and
 * routing it through `Number()` to format it would undo that at the last step —
 * silently, and only for values large or awkward enough to lose a digit.
 *
 * The grouping is inserted into the digit string directly, so what is displayed
 * is exactly what the ledger holds.
 */

const THOUSANDS = '.';
const DECIMAL = ',';

/**
 * Format a decimal string as Danish currency, without a symbol.
 *
 * `"1234.5"` → `"1.234,50"`, `"-1234.5"` → `"-1.234,50"`.
 */
export function formatMoney(value: string | Decimal, decimals = 2): string {
  const decimal = value instanceof Decimal ? value : new Decimal(value);
  const negative = decimal.isNegative();
  // toFixed on a Decimal is exact and does not go via a float.
  const fixed = decimal.abs().toFixed(decimals);
  const [whole = '0', fraction] = fixed.split('.');

  let grouped = '';
  for (let index = 0; index < whole.length; index += 1) {
    // Count from the right, so a separator lands every three digits.
    if (index > 0 && (whole.length - index) % 3 === 0) {
      grouped += THOUSANDS;
    }
    grouped += whole[index];
  }

  const body = fraction === undefined ? grouped : `${grouped}${DECIMAL}${fraction}`;
  return negative ? `-${body}` : body;
}

/**
 * Parse what someone typed into a `Decimal`.
 *
 * A Danish keyboard produces `1.234,56`; a numeric keypad produces `1234.56`.
 * Both have to work, which means deciding which character is the decimal
 * separator rather than stripping one of them blindly — getting that wrong
 * reads `1234.56` as `123456`, a hundredfold error on an immutable ledger.
 *
 * The rule:
 *
 * | Input | Read as | Why |
 * |---|---|---|
 * | `1.234,56` | 1234.56 | both present — the **last** separator is the decimal one |
 * | `1234,56` | 1234.56 | one comma — decimal |
 * | `1234.56` | 1234.56 | one full stop — decimal |
 * | `1.234.567` | 1234567 | repeated separators can only be grouping |
 * | `1.234` | 1.234 | **ambiguous**; read as a decimal, never as grouping |
 *
 * The last row is the only genuine ambiguity, and it is resolved towards the
 * smaller number on purpose: the form echoes the parsed amount back formatted,
 * so `1,23` is visible and correctable, and an entry has to balance before it
 * can be posted.
 *
 * Returns null when the input is not a number — the caller decides whether that
 * is an error or simply an empty field.
 */
export function parseMoney(input: string): Decimal | null {
  const trimmed = input.trim().replace(/\s/gu, '');
  if (trimmed === '') {
    return null;
  }
  if (!/^-?[\d.,]+$/u.test(trimmed)) {
    return null;
  }

  const lastComma = trimmed.lastIndexOf(',');
  const lastDot = trimmed.lastIndexOf('.');
  const commas = (trimmed.match(/,/gu) ?? []).length;
  const dots = (trimmed.match(/\./gu) ?? []).length;

  let decimalAt = -1;
  if (commas > 0 && dots > 0) {
    decimalAt = Math.max(lastComma, lastDot);
  } else if (commas === 1) {
    decimalAt = lastComma;
  } else if (dots === 1) {
    decimalAt = lastDot;
  }
  // Anything else (none, or several of one kind) is grouping only.

  const wholeRaw = decimalAt === -1 ? trimmed : trimmed.slice(0, decimalAt);
  const fraction = decimalAt === -1 ? '' : trimmed.slice(decimalAt + 1);

  // Whatever separators remain in the whole part can only be grouping, and
  // grouping is three digits at a time. Rejecting anything else catches typos
  // like `12,34,56`, which would otherwise be read as 123456.
  if (/[.,]/u.test(wholeRaw) && !/^-?\d{1,3}([.,]\d{3})+$/u.test(wholeRaw)) {
    return null;
  }
  const whole = wholeRaw.replace(/[.,]/gu, '');

  // A separator cannot appear on both sides of the decimal one.
  if (/[.,]/u.test(fraction)) {
    return null;
  }

  const normalised = fraction === '' ? whole : `${whole}.${fraction}`;
  if (!/^-?\d+(\.\d+)?$/u.test(normalised)) {
    return null;
  }

  try {
    const decimal = new Decimal(normalised);
    return decimal.isFinite() ? decimal : null;
  } catch {
    return null;
  }
}

/** `"2026-01-15"` → `"15.01.2026"`, the Danish convention. */
export function formatDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  if (year === undefined || month === undefined || day === undefined) {
    return iso;
  }
  return `${day}.${month}.${year}`;
}

/**
 * `"2026-01-15T09:30:00.000Z"` -> `"15.01.2026 10:30"`, in the reader's own
 * time zone.
 *
 * Local, deliberately, and the opposite choice from `toIsoDate` for the
 * opposite reason. That one keeps a *chosen* date out of UTC so an evening
 * entry does not slide into the previous day. This one converts an *observed*
 * instant into local time, because "sidst brugt 22:14" means nothing if the
 * reader is not on UTC.
 *
 * Returns the input unchanged if it is not a timestamp, rather than the
 * "Invalid Date" a Date would render.
 */
export function formatDateTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return iso;
  }
  const day = String(at.getDate()).padStart(2, '0');
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const year = String(at.getFullYear()).padStart(4, '0');
  const hours = String(at.getHours()).padStart(2, '0');
  const minutes = String(at.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

/** A `Date` from a date input, as the `YYYY-MM-DD` the API expects. */
export function toIsoDate(date: Date): string {
  // Deliberately local, not toISOString(): the latter converts to UTC, so a
  // date picked late in the evening in Copenhagen becomes the previous day and
  // the entry lands in the wrong month — or the wrong VAT period.
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
